import React, { useCallback, useEffect, useState } from 'react'
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useSelector } from 'react-redux'
import { useRoute } from '@react-navigation/core'
import moment from 'moment'

import PageTitle from '../../components/tags/PageTitle'
import MainContainer from '../../components/MainContainer/MainContainer'
import { formatForMetaTitle } from '../../components/text/TextFormatter'
import LoadingSpinner from '../../components/activityIndicators/LoadingSpinner'
import { CustomText } from '../../components/text/StyledText'
import { ButtonPrimary } from '../../components/buttons/StyledButtons'

import IconNames from '../../constants/IconNames'
import NormalIcon from '../../components/icons/NormalIcon'

import Paths from '../../constants/Paths'
import Layout from '../../constants/Layout'
import Colors from '../../constants/Colors'
import { liftedShadow } from '../../styles/shadows'
import { CornerStyles } from '../../styles'

import * as EventService from '../../services/api/Events'
import { openMap } from '../../utils/openMap'
import { txt } from '../../locales/i18n'
import { URLOpener } from "../../utils/URLOpener"
import { addLinkToText } from '../../utils/addLinkToText.js'
import { getUniverseConfig } from "../../universe/universeController"

export const EventScreen = () => {
  const user = useSelector(({ user }) => user)
  moment.locale(user.language)

  const route = useRoute()
  const { eventId, universe } = route.params

  const universeObject = getUniverseConfig(universe?.toLowerCase())

  const [event, setEvent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const imageSource = Paths.imageEventUrlBase + event?.imageSource

  const handleOpenMap = () => {
    openMap(event.address, event.postalCode)
  }

  const getEvent = useCallback(async () => {
    setIsLoading(true)

    const event = await EventService.getEvent(eventId)
    setEvent(event)

    setIsLoading(false)
  }, [eventId])

  useEffect(() => {
    getEvent()
  }, [getEvent])

  const handleOpenEvent = () => {
    if (event?.totalTickets) URLOpener(`${Paths.getBaseUrl()}/oplev/${eventId}/checkout`)
    else URLOpener(event.bookingLink)
  }

  return (
    <>
      <PageTitle title={event ? formatForMetaTitle(event?.title) : 'placeholder'} />
      <MainContainer backgroundColor={universeObject?.backgroundColor}>
        <View style={styles.container}>
          {event && (
            <View style={styles.eventContainer}>
              <View>
                <Image
                  resizeMode="cover"
                  accessibilityIgnoresInvertColors
                  style={styles.coverBackground}
                  source={{ uri: imageSource }}
                  blurRadius={6}
                />

                <Image
                  resizeMode="contain"
                  style={styles.coverForeground}
                  accessibilityIgnoresInvertColors
                  source={{ uri: imageSource }}
                />
              </View>

              <View style={styles.textContainer}>
                <View style={styles.topContainer}>
                  <CustomText font="bold" style={styles.title}>
                    {event.title}
                  </CustomText>

                  <CustomText font="small">
                    {moment(event.dateStart).format('lll')} - {moment(event.dateEnd).format('LT')}
                  </CustomText>
                </View>

                <CustomText font="small">{addLinkToText(event.body)}</CustomText>

                <View style={styles.detailsContainer}>
                  <View style={styles.detailContainer}>
                    <NormalIcon stroke="fad" size={48} name={IconNames.users} color={Colors.blueAzure} />
                    <CustomText font="smallBold" style={styles.detailText}>
                      {txt('event.organizer')}
                    </CustomText>
                    <CustomText font="small" style={styles.detailInfo}>
                      {event.organizer}
                    </CustomText>
                  </View>

                  <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel={txt('event.openAddressLabel')}
                    accessibilityHint={txt('event.openAddressHint')}
                    onPress={handleOpenMap}
                    style={styles.detailContainer}
                  >
                    <NormalIcon stroke="fad" size={48} name={IconNames.mapMarker} color={Colors.blueAzure} />
                    <CustomText font="smallBold" style={styles.detailText}>
                      {txt('event.address')}
                    </CustomText>
                    <CustomText font="small" style={styles.detailInfo}>
                      {event.address}, {event.postalCode} {event.city}
                    </CustomText>
                  </TouchableOpacity>

                  <View style={styles.detailContainer}>
                    <NormalIcon stroke="fad" size={48} name={IconNames.ticket} color={Colors.blueAzure} />
                    <CustomText font="smallBold" style={styles.detailText}>
                      {txt('event.ticketPrice')}
                    </CustomText>
                    <CustomText font="small" style={styles.detailInfo}>
                      {event.price} kr.
                    </CustomText>
                  </View>
                </View>

                <ButtonPrimary
                  accessibilityRole="button"
                  accessibilityLabel={txt('event.openBookingLinkLabel')}
                  accessibilityHint={txt('event.openBookingLinkHint')}
                  title={txt('event.buyTicket')}
                  onPress={handleOpenEvent}
                  style={styles.bookingLinkBotton}
                />
              </View>
            </View>
          )}

          {isLoading && <LoadingSpinner />}
        </View>
      </MainContainer>
    </>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: 12,
    width: '100%',
    alignSelf: 'center',
    marginVertical: 'auto',
    maxWidth: Layout.maxFeedWidth,
  },

  eventContainer: {
    overflow: 'hidden',
    backgroundColor: Colors.white,
    ...CornerStyles.borderRadiusS,
    ...liftedShadow,
  },

  coverBackground: {
    position: 'absolute',
    top: 0,
    height: '100%',
    width: '100%',
  },

  coverForeground: {
    height: 200,
    margin: 12,
    ...liftedShadow,
  },

  topContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    flexWrap: 'wrap',
  },

  textContainer: {
    flex: 1,
    margin: 24,
  },

  title: {
    marginRight: 12,
  },

  detailsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    marginVertical: 12,
    flexWrap: 'wrap',
  },

  detailContainer: {
    alignItems: 'center',
    flex: 1,
    minWidth: 120,
    margin: 6,
  },

  detailText: {
    marginVertical: 3,
    textAlign: 'center',
  },

  detailInfo: {
    textAlign: 'center',
  },

  bookingLinkBotton: {
    backgroundColor: Colors.blueAzure,
  },
})
